import { QueryClient } from '@tanstack/react-query';

import { ApiError, type Session } from './api';

/**
 * Lo que una sesión carga, aislado de la siguiente.
 *
 * Antes había **un** `QueryClient` para toda la vida de la pestaña, creado en
 * `main.tsx`, y las consultas se guardaban con claves sin dueño —`['resumen']`,
 * `['insumos']`—. Al salir de un negocio y entrar a otro en el mismo equipo, el
 * segundo veía el resumen del primero hasta recargar: el 8 de octubre, quien
 * entró como la tienda de tecnología vio el lomo y la pechuga del restaurante.
 * El servidor no se equivocaba; era el navegador enseñando lo que ya tenía.
 *
 * Ahora cada sesión abre su propio espacio: un cliente nuevo, que nace vacío,
 * y una llave con la que la aplicación entera se vuelve a montar. Al salir —o
 * al caducar la sesión sin poder renovarse— el espacio se cierra: se cancela lo
 * que estaba en vuelo y se borra lo guardado. Un cliente nuevo y no solo un
 * `clear()` del de siempre porque una consulta que sigue en vuelo al salir
 * vuelve después y se guarda en el cliente que la pidió: si es el mismo que usa
 * la sesión siguiente, el dato del negocio anterior vuelve a aparecer.
 */
export interface EspacioDeSesion {
  readonly cliente: QueryClient;

  /**
   * Con qué llave se monta la aplicación de esta sesión. Distinta en cada
   * entrada, aunque sea la misma persona en el mismo local: el estado de las
   * pantallas —un filtro, un cajón abierto— tampoco pasa de una a otra.
   */
  readonly llave: string;
}

let entradas = 0;

export function abrirEspacio(session: Session): EspacioDeSesion {
  entradas += 1;
  return {
    cliente: crearClienteDeConsultas(),
    llave: `${session.user.id}:${session.scope.branchId}:${entradas}`,
  };
}

export function cerrarEspacio(espacio: EspacioDeSesion): void {
  void espacio.cliente.cancelQueries();
  espacio.cliente.clear();
}

/**
 * Cuándo reintentar y cuándo no.
 *
 * Es la misma distinción que hace el POS y por la misma razón: «no se pudo
 * preguntar» se reintenta, «el servidor dijo que no» no. Reintentar un 403 o un
 * 422 repetiría el mismo no para siempre y dejaría la pantalla girando delante
 * de alguien que no puede hacer nada.
 */
export function crearClienteDeConsultas(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (intentos, error) =>
          error instanceof ApiError && error.isRetryable && intentos < 2,
        // El almacén no cambia cada segundo: quien recibe mercadería tarda
        // minutos en cada línea. Refrescar en cada foco sería gastar por gastar.
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
      mutations: { retry: false },
    },
  });
}
